import { Assets, Texture, type Spritesheet } from 'pixi.js';
import { allFileFields, assertLayerStackManifest, type LayerStackManifest } from './ManifestTypes';

/**
 * Loads one rung directory: manifest.json plus every PNG any of its draw lists
 * can reference, keyed by the manifest's own `file` field. Paths are resolved
 * against the manifest's directory and nothing else — that is the only base
 * schema v2 has. Composites are never fetched; the stack rebuilds them.
 */
export interface LayerStackAsset {
  /** Directory the manifest was read from, with a trailing slash. */
  readonly rungPath: string;
  readonly manifest: LayerStackManifest;
  /** manifest `file` field -> texture. */
  readonly textures: ReadonlyMap<string, Texture>;
}

/** Where scripts/build-r2-assets.mjs ships the rung directories. */
export const R2_ASSET_ROOT = 'assets/r2/';
export const R2_INDEX_URL = `${R2_ASSET_ROOT}index.json`;

export interface R2IndexRung {
  readonly rung: string;
  /** Runtime-relative rung directory, ready for loadLayerStack. */
  readonly path: string;
  readonly label: string;
  readonly target: readonly [number, number];
  readonly states: readonly string[];
  readonly hasPulse: boolean;
}

export interface R2IndexAsset {
  readonly slug: string;
  readonly asset: string;
  readonly facing: string;
  readonly rungs: readonly R2IndexRung[];
}

export interface R2Index {
  readonly schemaVersion: number;
  readonly generated: string;
  readonly assets: readonly R2IndexAsset[];
}

const cache = new Map<string, Promise<LayerStackAsset>>();
let losslessAtlasMode = false;

function asDirectory(rungPath: string): string {
  return rungPath.endsWith('/') ? rungPath : `${rungPath}/`;
}

async function fetchLayerStack(directory: string): Promise<LayerStackAsset> {
  const source = `${directory}manifest.json`;
  const response = await fetch(source);
  if (!response.ok) throw new Error(`manifest fetch ${response.status} for ${source}`);
  const manifest = assertLayerStackManifest(await response.json(), source);
  const files = allFileFields(manifest);
  if (losslessAtlasMode && manifest.rung === 'RT') {
    const atlasUrl = `${directory}atlas.json`;
    const sheet = await Assets.load(atlasUrl) as Spritesheet;
    const textures = new Map<string, Texture>();
    const sheets = [sheet, ...sheet.linkedSheets];
    const framePrefix = `${manifest.slug}/`;
    for (const linked of sheets) {
      for (const [frameName, texture] of Object.entries(linked.textures) as [string, Texture][]) {
        if (!frameName.startsWith(framePrefix)) continue;
        texture.source.scaleMode = 'nearest';
        textures.set(frameName.slice(framePrefix.length), texture);
      }
    }
    for (const file of files) {
      if (!textures.has(file)) throw new Error(`${source}: atlas missing raster ${file}`);
    }
    return { rungPath: directory, manifest, textures };
  }
  const urls = files.map((file) => directory + file);
  const loaded = await Assets.load(urls) as Record<string, Texture>;
  const textures = new Map<string, Texture>();
  for (let i = 0; i < files.length; i += 1) {
    const texture = loaded[urls[i]!];
    if (!texture) throw new Error(`${source}: missing raster ${urls[i]!}`);
    texture.source.scaleMode = 'nearest';
    textures.set(files[i]!, texture);
  }
  return { rungPath: directory, manifest, textures };
}

/** R2Renderer sets this once before the first stack load. Desktop remains on individual PNGs. */
export function setLayerStackAtlasMode(enabled: boolean): void {
  losslessAtlasMode = enabled;
}

/** Cached by rung path, so two actors on the same rung share one texture set. */
export function loadLayerStack(rungPath: string): Promise<LayerStackAsset> {
  const directory = asDirectory(rungPath);
  const cached = cache.get(directory);
  if (cached) return cached;
  const pending = fetchLayerStack(directory).catch((error: unknown) => {
    cache.delete(directory);
    throw error;
  });
  cache.set(directory, pending);
  return pending;
}

/**
 * Which assets and rungs were actually shipped. Rungs are discovered here, never
 * hardcoded, so a rung generated later shows up after a rebuild with no code edit.
 */
export async function loadR2Index(): Promise<R2Index> {
  const response = await fetch(R2_INDEX_URL);
  if (!response.ok) throw new Error(`r2 index fetch ${response.status} for ${R2_INDEX_URL} — run: bun run assets:r2`);
  return await response.json() as R2Index;
}

export function unloadLayerStack(rungPath: string): void {
  const directory = asDirectory(rungPath);
  const pending = cache.get(directory);
  cache.delete(directory);
  if (!pending) return;
  void pending.then((asset) => {
    if (losslessAtlasMode && asset.manifest.rung === 'RT') {
      void Assets.unload(`${directory}atlas.json`);
    } else {
      void Assets.unload([...asset.textures.keys()].map((file) => directory + file));
    }
  }, () => undefined);
}

/** Bundle lifecycle owns the atlas resource; this only invalidates the decoded manifest/texture view. */
export function forgetLayerStack(rungPath: string): void {
  cache.delete(asDirectory(rungPath));
}

export function clearLayerStackCache(): void {
  for (const rungPath of [...cache.keys()]) unloadLayerStack(rungPath);
}
